import { cn } from '@/lib/cn';
import {
  STOCKING_LABELS,
  fmtDensity,
  fmtInt,
  fmtSigned,
  parseStockingText,
  previewFigures,
  type StockingFieldKey,
  type StockingFieldText,
} from '@/lib/stockingFields';

interface Props {
  text: StockingFieldText;
  onChange: (key: StockingFieldKey, value: string) => void;
  /** Delivered = the stocked count typed in the form above (null until it is a valid number). */
  count: number | null;
  areaM2: number | null;
  idPrefix: string;
  /** Phone layout: 48px fields and 16px text whatever the width (a landscape phone is wider than `sm`). */
  phone?: boolean;
}

const FIELDS: Array<{ key: StockingFieldKey; numeric: boolean; placeholder?: string }> = [
  { key: 'pieces_ordered', numeric: true, placeholder: 'e.g. 350000' },
  { key: 'ordered_allowance_pct', numeric: true, placeholder: '0 to 100' },
  { key: 'avg_count', numeric: true },
  { key: 'containers', numeric: true },
  { key: 'container_type', numeric: false },
  { key: 'cv_pct', numeric: true },
];

/** Optional stocking details shared by Mark as stocked, Start crop and Edit details. */
export function StockingFields({ text, onChange, count, areaM2, idPrefix, phone }: Props) {
  const { payload, errors } = parseStockingText(text);
  const preview = previewFigures(count, payload.pieces_ordered, payload.ordered_allowance_pct, areaM2);
  const field = cn('w-full rounded-lg border border-gray-300 px-3', phone ? 'min-h-12 text-base' : 'min-h-12 text-base sm:min-h-11 sm:text-sm');
  return (
    <fieldset className="flex flex-col gap-3">
      <legend className="text-sm font-semibold text-gray-700">
        Ordering details <span className="font-normal text-gray-500">· optional</span>
      </legend>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {FIELDS.map(({ key, numeric, placeholder }) => (
          <label key={key} htmlFor={`${idPrefix}-${key}`} className="flex flex-col gap-1.5 text-sm font-semibold text-gray-700">
            {STOCKING_LABELS[key]}
            <input
              id={`${idPrefix}-${key}`}
              inputMode={numeric ? 'decimal' : 'text'}
              autoComplete="off"
              aria-invalid={errors[key] ? true : undefined}
              aria-describedby={errors[key] ? `${idPrefix}-${key}-err` : undefined}
              className={cn(field, numeric && 'tabular-nums', errors[key] && 'border-red-500')}
              value={text[key]}
              placeholder={placeholder}
              onChange={(e) => onChange(key, e.target.value)}
            />
            {errors[key] && (
              <span id={`${idPrefix}-${key}-err`} className="text-xs font-normal text-red-700">
                {errors[key]}
              </span>
            )}
          </label>
        ))}
      </div>
      {(preview.extra !== null || preview.density !== null) && (
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 rounded-lg bg-gray-50 px-3 py-2 text-sm" aria-live="polite">
          {preview.extra !== null && (
            <>
              <dt className="text-gray-500">Extra</dt>
              <dd className="tabular-nums">
                {fmtSigned(preview.extra)} pcs{preview.extraPct !== null ? ` (${preview.extraPct >= 0 ? '+' : ''}${preview.extraPct.toFixed(1)}% of ordered)` : ''}
              </dd>
            </>
          )}
          {preview.withAllowance !== null && (
            <>
              <dt className="text-gray-500">Ordered + allowance</dt>
              <dd className="tabular-nums">{fmtInt(preview.withAllowance)} pcs</dd>
            </>
          )}
          {preview.density !== null && (
            <>
              <dt className="text-gray-500">Density</dt>
              <dd className="tabular-nums">{fmtDensity(preview.density)}</dd>
            </>
          )}
        </dl>
      )}
    </fieldset>
  );
}
